import React, { ChangeEvent, useEffect, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Grow,
  Snackbar,
  TextField,
} from '@mui/material';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';
import { DMSDeal } from '@upstart/harmony/lib/legos/dmsTypes';
import {
  FinanceApplication,
  FinanceResponse,
  UpstartApplicationStatus,
} from '@upstart/harmony/lib/legos/financingTypes';
import { FinanceApplicationStatus } from '@upstart/harmony/lib/constants';
import DealService from 'services/DealService';
import PencilService from 'services/PencilService';
import FinanceApplicationService from 'services/FinanceApplicationService';
import FinanceResponseService from 'services/FinanceResponseService';
import DMSService from 'services/DMSService';
import { useHistory, useParams } from 'react-router-dom';
import loginActions from '../../redux/logins/actions';
import logo from '../../assets/logo.png';
import { JsonDialog } from './JsonDialog';
import { CreditApplications } from './CreditApplications';
import './LendingSupport.scss';
import { LendingSupportDealer } from './Dealer';
import { LendingSupportCar } from './Car';
import { LendingSupportDeal } from './Deal';
import { LendingSupportPencils } from './Pencils';
import { LendingSupportDeskingOffers } from './DeskingOffers';
import { LendingSupportDataDogLinks } from './DataDogLinks';
import { LendingSupportCloudWatchLinks } from './CloudWatchLinks';

interface Params {
  dealId: string;
}

export interface CreditApp {
  financeApplication: FinanceApplication;
  financeResponses?: (FinanceResponse & { isSelected?: boolean })[] | null;
}

/**
 * Main component that renders the Lending Support Tool, showing clickable
 * JSON data for a given deal.
 */
export const LendingSupport: React.FC = () => {
  const { dealId } = useParams<Params>();
  const history = useHistory();

  const [dealIdInput, setDealIdInput] = useState<string>(dealId ?? '');
  const [deal, setDeal] = useState<Deal | undefined>();
  const [pencils, setPencils] = useState<Pencil[] | null>();
  const [creditApps, setCreditApps] = useState<CreditApp[] | null>();
  const [financeApplications, setFinanceApplications] = useState<
    FinanceApplication[] | null
  >();
  const [financeResponses, setFinanceResponses] = useState<
    FinanceResponse[] | null | undefined
  >(undefined);
  const [jsonData, setJsonData] = useState<object | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // Fetch pencils
  useEffect(() => {
    const carId = deal?.car_id;
    if (!deal?.dealer_id || !carId) {
      setPencils(undefined);
      return;
    }
    PencilService.getPencilList(deal.id, null)
      .then((results?: { pencilList?: Pencil[] }) => {
        const carPencils = results?.pencilList?.filter(p => p.carId === carId);
        setPencils(carPencils ?? null);
      })
      .catch(() => {
        setPencils(undefined);
        setAlertMessage('Unable to fetch pencils');
      });
  }, [deal]);

  // Fetch applications
  useEffect(() => {
    if (!deal?.car_id || !deal?.id) {
      return;
    }
    FinanceApplicationService.get(deal.id, deal.car_id, [
      FinanceApplicationStatus.ACTIVE,
      FinanceApplicationStatus.INACTIVE,
      FinanceApplicationStatus.FAILED,
    ])
      .then((results?: FinanceApplication[]) => {
        if (!results?.length) {
          setCreditApps(null);
          setFinanceApplications(null);
          return;
        }

        const applications = results.map(financeApplication => ({
          financeApplication,
        }));
        setCreditApps(applications);
        setFinanceApplications(results);
      })
      .catch(() => {
        setCreditApps(null);
        setFinanceApplications(null);
        setAlertMessage('Unable to fetch finance applications');
      });
  }, [deal]);

  useEffect(() => {
    if (
      financeApplications?.length &&
      creditApps?.length &&
      financeResponses === undefined
    ) {
      FinanceResponseService.getForApplications(
        financeApplications.map(a => a.id),
      )
        .then((results: FinanceResponse[]) => {
          const updatedCreditApps: CreditApp[] = creditApps.map(app => ({
            ...app,
            financeResponse: null,
          }));
          if (results?.length) {
            results.forEach((financeResponse, index) => {
              const creditApp = updatedCreditApps.find(
                app =>
                  app.financeApplication.id === financeResponse.applicationId,
              );
              const updatedFinanceResponse = {
                ...financeResponse,
                isSelected:
                  deal?.selectedFinanceResponseId === financeResponse.id,
              };
              if (creditApp) {
                creditApp.financeResponses = [
                  ...(creditApp.financeResponses ?? []),
                  updatedFinanceResponse,
                ];
              }
            });
            // sort financeResponses
            updatedCreditApps.forEach(app => {
              if (app.financeResponses) {
                app.financeResponses = app.financeResponses.sort((a, b) => {
                  const c = new Date(a.updatedAt ?? '');
                  const d = new Date(b.updatedAt ?? '');
                  return d.getTime() - c.getTime();
                });
              }
            });
            setCreditApps(updatedCreditApps);
            setFinanceResponses(results);
          } else {
            setFinanceResponses(null);
          }
        })
        .catch(() => {
          setFinanceResponses(null);
          setAlertMessage('Unable to fetch finance responses');
        });
    }
  }, [deal, financeApplications, financeResponses]);

  useEffect(() => {
    if (dealId) {
      loadHandler();
    }
  }, [dealId]);

  const clearLocalState = () => {
    setDeal(undefined);
    setPencils(undefined);
    setCreditApps(undefined);
    setFinanceApplications(undefined);
    setFinanceResponses(undefined);
  };

  const loadHandler = () => {
    if (dealIdInput) {
      clearLocalState();
      DealService.getDeal(dealIdInput)
        .then(dealObj => {
          setDeal(dealObj);
        })
        .catch(() => {
          // error fetching deal, so set everything to null
          clearLocalState();
          setAlertMessage('Unable to fetch deal');
        });
    }
  };

  const redirectDeal = () => {
    if (dealIdInput) {
      history.push(`/lending-support/${dealIdInput}`);
    }
  };

  const onEntityClick = (data?: object | null) => {
    if (data) {
      setJsonData(data);
      setIsDialogOpen(true);
    }
  };

  const fetchStatus = (providerApplicationId: string) => () => {
    if (!creditApps) {
      return;
    }
    setIsLoading(true);
    FinanceApplicationService.getApplicationStatuses([providerApplicationId])
      .then((results: UpstartApplicationStatus[]) => {
        if (results && results.length > 0) {
          setIsLoading(false);
          setJsonData(results[0]);
          setIsDialogOpen(true);
        }
      })
      .catch(() => {
        setIsLoading(false);
        setAlertMessage('Unable to fetch status');
      });
  };

  const fetchDmsDeal = (dealObj: Deal, dmsDealId: string) => () => {
    setIsLoading(true);
    DMSService.pullFromDMS(dealObj, dmsDealId)
      .then((dmsDeal: DMSDeal) => {
        setIsLoading(false);
        setJsonData(dmsDeal);
        setIsDialogOpen(true);
      })
      .catch(() => {
        setIsLoading(false);
        setAlertMessage('Unable to fetch DMS deal');
      });
  };

  const renderDealId = () => (
    <div className="lending-support-dealid-container">
      <TextField
        id="dealId"
        data-testid="lending-support-dealid-textfield"
        label="Deal ID"
        value={dealIdInput}
        size="small"
        InputLabelProps={{
          shrink: true,
          sx: {
            fontSize: 12,
          },
        }}
        inputProps={{ style: { fontSize: 14 } }}
        sx={{ width: '38ch' }}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setDealIdInput(event.target.value);
        }}
      />
      <Button variant="contained" onClick={redirectDeal}>
        Load
      </Button>
    </div>
  );

  const renderDeal = () => (
    <>
      {deal && (
        <div className="lending-support-deal-container">
          <LendingSupportDataDogLinks deal={deal} />
          <LendingSupportCloudWatchLinks deal={deal} />
        </div>
      )}
      <div className="lending-support-deal-container">
        <LendingSupportDeal deal={deal} onClick={onEntityClick} />
        <LendingSupportDealer
          dealerId={deal?.dealer_id}
          onClick={onEntityClick}
          onError={setAlertMessage}
        />
        <LendingSupportCar
          carId={deal?.car_id}
          onClick={onEntityClick}
          onError={setAlertMessage}
        />
      </div>
      <div className="lending-support-deal-container">
        <LendingSupportPencils pencils={pencils} onClick={onEntityClick} />
        <LendingSupportDeskingOffers
          carId={deal?.car_id ?? undefined}
          dealId={deal?.id}
          pencilVersion={pencils?.[0]?.data?.version}
          onClick={onEntityClick}
          onError={setAlertMessage}
        />
      </div>
      {creditApps && (
        <>
          <div className="lending-support-header">Credit Applications</div>
          <div className="lending-support-credit-apps-container">
            <CreditApplications
              deal={deal}
              creditAppList={creditApps}
              onClick={onEntityClick}
              fetchStatus={fetchStatus}
              fetchDmsDeal={fetchDmsDeal}
            />
          </div>
        </>
      )}
      <JsonDialog
        open={isDialogOpen}
        data={jsonData ?? {}}
        onClose={() => setIsDialogOpen(false)}
      />
      <Backdrop
        sx={{ color: '#aaa', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <div className="lending-support-loader-container">
          <CircularProgress color="inherit" />
        </div>
      </Backdrop>
    </>
  );

  return (
    <div
      className="lending-support-container"
      data-testid="lending-support-container"
    >
      <div className="lending-support-logo-container">
        <img src={logo} alt="Logo" className="logo" />
      </div>
      <div className="lending-support-title">Lending Support Tool</div>
      {renderDealId()}
      <Snackbar
        open={!!alertMessage}
        autoHideDuration={4000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        TransitionComponent={Grow}
        onClose={() => setAlertMessage('')}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          <span style={{ fontSize: '14px' }}>{alertMessage}</span>
        </Alert>
      </Snackbar>
      {dealId && renderDeal()}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  auth: state.auth,
  logins: state.logins,
  user: state.user,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators({ ...loginActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(LendingSupport);
